/**
 * Hire Requests Page
 *
 * Manage incoming and outgoing hire requests.
 */

import { useState } from 'react';
import {
  useGetMyHireRequestsQuery,
  useGetMySentHireRequestsQuery,
  useGetHireRequestStatsQuery,
  useUpdateHireRequestStatusMutation,
  HireRequestStatus,
} from '../store/api/marketplaceApi';

type TabType = 'received' | 'sent';

export function HireRequestsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('received');
  const [statusFilter, setStatusFilter] = useState<HireRequestStatus | ''>('');

  const { data: receivedRequests, isLoading: receivedLoading } = useGetMyHireRequestsQuery({
    status: statusFilter || undefined,
  });
  const { data: sentRequests, isLoading: sentLoading } = useGetMySentHireRequestsQuery({
    status: statusFilter || undefined,
  });
  const { data: stats } = useGetHireRequestStatsQuery();
  const [updateStatus, { isLoading: isUpdating }] = useUpdateHireRequestStatusMutation();
  const [updateError, setUpdateError] = useState<string | null>(null);

  const isLoading = activeTab === 'received' ? receivedLoading : sentLoading;
  const requests = activeTab === 'received' ? receivedRequests?.requests : sentRequests?.requests;

  const handleStatusUpdate = async (id: string, status: 'accepted' | 'rejected' | 'closed') => {
    setUpdateError(null);
    try {
      await updateStatus({ id, status }).unwrap();
    } catch {
      setUpdateError('Failed to update request status. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Hire Requests</h1>
        <p className="mt-1 text-gray-500">
          Manage your incoming and outgoing hire requests
        </p>
      </div>

      {/* Update error */}
      {updateError && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {updateError}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Received"
            value={stats.received.total}
            subtext={`${stats.received.pending} pending`}
          />
          <StatCard
            label="Accepted"
            value={stats.received.accepted}
            color="green"
          />
          <StatCard
            label="Sent"
            value={stats.sent.total}
            subtext={`${stats.sent.pending} pending`}
          />
          <StatCard
            label="Your Response Rate"
            value={
              stats.received.total > 0
                ? `${Math.round(((stats.received.accepted + stats.received.rejected) / stats.received.total) * 100)}%`
                : '-'
            }
            color="blue"
          />
        </div>
      )}

      {/* Tabs and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          <button
            onClick={() => setActiveTab('received')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'received'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Received
            {stats && stats.received.pending > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs">
                {stats.received.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'sent'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Sent
          </button>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as HireRequestStatus | '')}
          className="input w-auto"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Requests List */}
      <div className="card">
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : requests && requests.length > 0 ? (
            <div className="space-y-4">
              {requests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  isReceived={activeTab === 'received'}
                  onAccept={() => handleStatusUpdate(request.id, 'accepted')}
                  onReject={() => handleStatusUpdate(request.id, 'rejected')}
                  onClose={() => handleStatusUpdate(request.id, 'closed')}
                  isUpdating={isUpdating}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <InboxIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hire requests yet</p>
              <p className="text-sm text-gray-400 mt-1">
                {activeTab === 'received'
                  ? 'When someone sends you a hire request, it will appear here.'
                  : 'When you send hire requests, they will appear here.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number | string;
  subtext?: string;
  color?: 'gray' | 'green' | 'blue';
}

function StatCard({ label, value, subtext, color = 'gray' }: StatCardProps) {
  const colorClasses = {
    gray: 'bg-gray-50',
    green: 'bg-green-50',
    blue: 'bg-blue-50',
  };

  return (
    <div className={`p-4 rounded-lg ${colorClasses[color]}`}>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
      {subtext && <div className="text-xs text-gray-400 mt-1">{subtext}</div>}
    </div>
  );
}

interface RequestCardProps {
  request: {
    id: string;
    requesterUserId: string;
    targetUserId: string;
    portfolioId: string | null; // null once the portfolio is deleted
    message: string;
    context?: string;
    status: HireRequestStatus;
    createdAt: string;
    requester?: { id: string; email: string };
    target?: { id: string; email: string };
    portfolio?: { id: string; displayName?: string };
  };
  isReceived: boolean;
  onAccept: () => void;
  onReject: () => void;
  onClose: () => void;
  isUpdating: boolean;
}

function RequestCard({
  request,
  isReceived,
  onAccept,
  onReject,
  onClose,
  isUpdating,
}: RequestCardProps) {
  const [expanded, setExpanded] = useState(false);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    closed: 'bg-gray-100 text-gray-800',
  };

  // Get the other party's display name based on whether this is a received or sent request
  const displayName = isReceived
    ? request.requester?.email || 'Unknown'
    : request.portfolio?.displayName || request.target?.email || 'Unknown';

  return (
    <div className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
          <span className="font-medium text-primary-600">
            {displayName[0]?.toUpperCase() || '?'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 truncate">{displayName}</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[request.status]}`}>
              {request.status}
            </span>
          </div>
          <p className={`text-sm text-gray-600 ${expanded ? '' : 'line-clamp-2'}`}>
            {request.message}
          </p>
          {request.message.length > 150 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary-600 hover:text-primary-700 mt-1"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>{new Date(request.createdAt).toLocaleDateString()}</span>
            {request.context && <span>via {request.context}</span>}
          </div>
        </div>

        {/* Actions */}
        {isReceived && request.status === 'pending' && (
          <div className="flex gap-2">
            <button
              onClick={onAccept}
              disabled={isUpdating}
              className="btn btn-sm btn-primary"
            >
              Accept
            </button>
            <button
              onClick={onReject}
              disabled={isUpdating}
              className="btn btn-sm btn-outline text-red-600 border-red-200 hover:bg-red-50"
            >
              Decline
            </button>
          </div>
        )}
        {request.status === 'accepted' && (
          <button
            onClick={onClose}
            disabled={isUpdating}
            className="btn btn-sm btn-outline"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}
